import { getCurrentBreakpoint } from "../util/responsive"
import { formatSolItem } from "./SolAdsStyling"

export type AdSetting = {
    defaultOptions: string
    defaultSizeClass: string | null
    responsiveStep: string
}

export class SolAds {
    attributeObserver: MutationObserver
    newElementObserver: MutationObserver
    requestLock: boolean
    attributeObserverConfig: MutationObserverInit
    insertedElements: HTMLElement[]
    adFrames: Map<HTMLElement, AdSetting>
    scriptTimeoutIds: Map<HTMLElement, number>
    lastResponsiveStep: string
    sizeClass: string
    sizeClassExpression: RegExp
    sizeExpression: RegExp
    sectorsExpression: RegExp
    countAdsExpression: RegExp
    system: string
    partnerId: number
    subPartner: string
    cdnUrl: string
    options: object
    static Type = {
        DisplayAd: "displayAd",
        TextAd: "textAd",
    }

    static Options = {
        SubPartnerName: "subPartnerName",
        NumberAdverts: "advertCount",
        AdvertsType: "advertsType",
        ImageSize: "imageSize",
        Debug: "debug",
        UseIp: "useIp",
        AsIFrame: "asIFrame",
        Responsive: "responsive",
    }
    static Attributes = {
        Columns: "columns",
        FrameClass: "frameClass",
        ContainerClass: "containerClass",
        HideLabel: "hideLabel",
    }

    static ImageSizes = {
        ImageWidth: "imageWidth",
        ImageHeight: "imageHeight",

        // Verschiedene mögliche Größen der Banner
        // -- TextAd --
        $TextAd: { imageWidth: 240, imageHeight: 120 },
        // -- Grafishe Banner --
        $LeaderBoard: { imageWidth: 728, imageHeight: 90 },
        $WideSkyscraper: { imageWidth: 160, imageHeight: 600 },
        $HalfPage: { imageWidth: 300, imageHeight: 600 },
        $Billboard: { imageWidth: 970, imageHeight: 250 },
        $ZwischenseitePortrait: { imageWidth: 320, imageHeight: 480 },
        $ZwischenseiteLandscape: { imageWidth: 480, imageHeight: 320 },
        $Rechteck3zu1: { imageWidth: 300, imageHeight: 100 },
        $MobileLeaderBoard: { imageWidth: 300, imageHeight: 50 },
        $LargeLeaderBoard: { imageWidth: 970, imageHeight: 90 },
        $RechteckMedium: { imageWidth: 300, imageHeight: 250 },
        $MobileLargeBanner: { imageWidth: 320, imageHeight: 100 },
        $MobileLeaderBoardWider: { imageWidth: 320, imageHeight: 50 },
    }

    static Sector = {
        displayAd: 2009,
        textAd: 2007,
    }

    constructor() {
        this.attributeObserver = new MutationObserver(this.adInitialized.bind(this))
        this.newElementObserver = new MutationObserver(this.collectInsertedNodes.bind(this))
        this.requestLock = false
        this.attributeObserverConfig = {
            childList: true,
            subtree: false,
            attributes: false,
        }
        this.insertedElements = []
        this.adFrames = new Map()
        this.scriptTimeoutIds = new Map()
        this.lastResponsiveStep = ""
        this.sizeClass = "content-width-"
        this.sizeClassExpression = /content-width-\d+/
        this.sizeExpression = /^(\s*m3ads_(?:logo)?image(width|height)\s*=\s*)\d+(;\s*)$/gm
        this.sectorsExpression = /^(\s*m3ads_sectors\s*=\s*")\d+(";\s*)$/gm
        this.countAdsExpression = /^(\s*m3ads_numberadverts\s*=\s*)\d+(;\s*)$/gm
        this.system = "Solads"
        this.partnerId = 379946
        this.subPartner = "ladies"
        this.cdnUrl = "https://feed.solads.media/resources/scripts/jsAds-1.4.min.js"
        this.options = {}

        this.init()
    }

    init() {
        this.newElementObserver.observe(document.body, {
            subtree: true,
            childList: true,
        })

        $(".sol-frame").each(this.initializeAd.bind(this))
    }

    initializeAd(_: number, solFrame: HTMLElement) {
        if (!solFrame) {
            return
        }

        const solFrameElement = $(solFrame)
        const solOptionsElement = solFrameElement.find("script[data-params]")
        const adContainerElement = solFrameElement.closest(".sol-content")
        const oldSizeClass = adContainerElement.attr("class").match(this.sizeClassExpression)

        if (solFrameElement.is(".initialized") || !solOptionsElement) {
            return
        }

        solFrameElement.addClass("initialized")

        this.adFrames.set(solFrame, {
            defaultOptions: solOptionsElement.text(),
            defaultSizeClass: oldSizeClass !== null && oldSizeClass.length > 0 ? oldSizeClass.pop() : null,
            responsiveStep: "",
        })

        this.applyResponsiveFrameData(getCurrentBreakpoint(), this.adFrames.get(solFrame), solFrame)

        this.attributeObserver.observe(solFrame, this.attributeObserverConfig)

        this.generateScriptTag(solFrameElement)
    }

    adInitialized(mutationsList: MutationRecord[]) {
        for (const mutationIndex in Object.keys(mutationsList)) {
            const mutation = mutationsList[mutationIndex]
            const addedNodes = $(mutation.addedNodes)
            const frameElement = $(mutation.target)

            if (!addedNodes.length || !addedNodes.is(".m3_container") || frameElement.find("a").is(".sol-link")) {
                continue
            }

            frameElement.find(".m3_container").addClass("hide")

            formatSolItem(
                frameElement,
                ((frame) => (_, success) => {
                    if (success) {
                        frame.find(".m3_container").removeClass("hide")
                    }
                })(frameElement),
            )
        }
    }

    collectInsertedNodes(mutationsList: MutationRecord[]) {
        for (const mutationIndex in Object.keys(mutationsList)) {
            const mutation = mutationsList[mutationIndex]
            const addedNodes = mutation.addedNodes
            const nodeCount = addedNodes.length
            let nodeIndex = 0

            if (!nodeCount) {
                continue
            }

            for (; nodeIndex < nodeCount; nodeIndex++) {
                this.insertedElements.push(addedNodes[nodeIndex] as HTMLElement)
            }
        }

        if (!this.requestLock) {
            requestAnimationFrame(this.checkIsAdElementMutation.bind(this))
        }
    }

    checkIsAdElementMutation() {
        let checkElement = this.insertedElements.shift()

        this.requestLock = true

        while (undefined !== checkElement) {
            if (checkElement.classList?.contains("sol-content")) {
                this.initializeAd(-1, checkElement.querySelector(".sol-frame") as HTMLElement)
                checkElement = this.insertedElements.shift()
                continue
            }

            if (!(checkElement instanceof HTMLElement)) {
                checkElement = this.insertedElements.shift()
                continue
            }

            const childElementMatch = checkElement.querySelector(".sol-content")

            if (childElementMatch) {
                this.initializeAd(-1, checkElement.querySelector(".sol-content .sol-frame") as HTMLElement)
            }
            checkElement = this.insertedElements.shift()
        }

        this.requestLock = false
    }

    updateResponsiveAds() {
        if (this.lastResponsiveStep === getCurrentBreakpoint()) {
            return
        }

        this.lastResponsiveStep = getCurrentBreakpoint()
        this.adFrames.forEach(this.applyResponsiveFrameData.bind(this, getCurrentBreakpoint()))
    }

    applyResponsiveFrameData(currentStep: string, adSettings: any, adFrame: HTMLElement) {
        if (currentStep === adSettings.responsiveStep) {
            return
        }

        const adFrameElement = $(adFrame)
        const optionsElement = adFrameElement.find("script[data-params]")
        const adContainerElement = adFrameElement.closest(".sol-content")
        const oldSizeClass = adContainerElement.attr("class").match(this.sizeClassExpression)
        const isExoClickBanner = adFrameElement.hasClass("uncensored-banner-container")

        if (isExoClickBanner) {
            adSettings.responsiveStep = currentStep
            this.adFrames.set(adFrame, adSettings)

            if (oldSizeClass !== null && oldSizeClass.length > 0) {
                adContainerElement.removeClass(oldSizeClass.pop())
            }

            if (!adFrame.dataset[currentStep]) {
                adContainerElement.addClass(adSettings.defaultSizeClass)
            } else {
                const stepData = JSON.parse(adFrame.dataset[currentStep])

                if ("zoneId" in stepData) {
                    const zoneId = stepData.zoneId
                    const newOptions = {
                        zoneId: zoneId,
                        multiplication: 1,
                    }

                    // TODO add solAds template
                    const newSolFrame = $(Mustache.render(siteConfig.templates.solAds, newOptions))

                    adFrameElement.html(newSolFrame.find(".sol-frame"))
                }
            }

            return
        }

        adSettings.responsiveStep = currentStep
        this.adFrames.set(adFrame, adSettings)

        adFrameElement.find(".m3_container").remove()

        optionsElement.attr("data-params", "jsads")

        if (oldSizeClass !== null && oldSizeClass.length > 0) {
            adContainerElement.removeClass(oldSizeClass.pop())
        }

        if (!adFrame.dataset[currentStep]) {
            adContainerElement.addClass(adSettings.defaultSizeClass)
            optionsElement.text(adSettings.defaultOptions)
        } else {
            const stepData = JSON.parse(adFrame.dataset[currentStep])
            let newOptions = adSettings.defaultOptions

            if (SolAds.Options.ImageSize in stepData) {
                const imageSize = stepData[SolAds.Options.ImageSize]

                newOptions = newOptions
                    .replace(this.sizeExpression, "$1{{$2}}$3")
                    .replace("{{width}}", imageSize[SolAds.ImageSizes.ImageWidth])
                    .replace("{{height}}", imageSize[SolAds.ImageSizes.ImageHeight])

                adContainerElement.addClass(this.sizeClass + imageSize[SolAds.ImageSizes.ImageWidth])
            }

            if (SolAds.Options.AdvertsType in stepData) {
                const sectorId = stepData[SolAds.Options.AdvertsType]

                // @ts-expect-error TODO fix at some point
                newOptions = newOptions.replace(this.sectorsExpression, `$1${SolAds.Sector[sectorId]}$2`)
            }

            if (SolAds.Options.NumberAdverts in stepData) {
                const adsCount = stepData[SolAds.Options.NumberAdverts]

                newOptions = newOptions.replace(this.countAdsExpression, `$1${adsCount}$2`)
            }

            optionsElement.text(newOptions)
        }

        this.generateScriptTag(adFrameElement)
    }

    generateScriptTag(scriptContainer: typeof $) {
        const scriptElement = scriptContainer.get(0)
        const scriptContents = scriptContainer.children()
        let timeoutId = this.scriptTimeoutIds.get(scriptElement)

        if (!scriptContents.length) {
            return
        }

        if (timeoutId) {
            window.clearTimeout(timeoutId)
        }

        scriptContainer.find("script:not([data-params])").remove()

        const newSolScript = $("<script />")

        scriptContainer.append(newSolScript)
        timeoutId = window.setTimeout(() => {
            newSolScript.attr("src", this.cdnUrl)
        }, 15)

        this.scriptTimeoutIds.set(scriptElement, timeoutId)
    }
}
